import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";
import { toast } from "sonner";
import {
  useAddGroupMemberMutation,
  useGetGroupMembersQuery,
  useGetMembersQuery,
  useUpdateGroupMutation,
} from "../../infrastructure/api/membersGroupsApi";
import type { Group, Member } from "../../domain/entities/Member";
import { RemoveGroupMemberDialog } from "./RemoveGroupMemberDialog";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group: Group | null;
}

export const EditGroupDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  group,
}) => {
  const { t } = useTranslation("membersGroups");
  const [name, setName] = useState("");
  // Members picked here are added on Save; removing an existing member is
  // confirmed and applied straight away (RemoveGroupMemberDialog).
  const [addedIds, setAddedIds] = useState<string[]>([]);
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
  const [isSavingMembers, setIsSavingMembers] = useState(false);

  const { data: groupMembers = [], isFetching: groupMembersLoading } =
    useGetGroupMembersQuery(
      { groupId: group?.id ?? "" },
      { skip: !open || !group, refetchOnMountOrArgChange: true },
    );
  const { data: allMembers = [], isLoading: allMembersLoading } =
    useGetMembersQuery(undefined, { skip: !open });

  const [updateGroup, { isLoading: isUpdatingGroup }] = useUpdateGroupMutation();
  const [addGroupMember] = useAddGroupMemberMutation();
  const isLoading = isUpdatingGroup || isSavingMembers;

  useEffect(() => {
    if (group) {
      setName(group.name ?? "");
    }
  }, [group]);

  // Compare against the latest member list so a retry after a partly failed
  // save doesn't re-add members that were already added.
  const isInGroup = (id: string) => groupMembers.some((member) => member.id === id);
  const idsToAdd = addedIds.filter((id) => !isInGroup(id));

  const selectedMembers = [
    ...groupMembers,
    ...allMembers.filter((member) => idsToAdd.includes(member.id)),
  ];

  const addMember = (memberId: string) => {
    if (!addedIds.includes(memberId)) {
      setAddedIds([...addedIds, memberId]);
    }
  };

  const removeMember = (member: Member) => {
    // Not saved yet: just drop it from the pending additions.
    if (idsToAdd.includes(member.id)) {
      setAddedIds(addedIds.filter((id) => id !== member.id));
    } else {
      setMemberToRemove(member);
    }
  };

  const handleClose = () => {
    setAddedIds([]);
    setMemberToRemove(null);
    onOpenChange(false);
  };

  const handleSave = async () => {
    if (!group || !name.trim()) return;
    try {
      setIsSavingMembers(true);
      await Promise.all(
        idsToAdd.map((userId) =>
          addGroupMember({ groupId: group.id, userId }).unwrap(),
        ),
      );
      setIsSavingMembers(false);
      // Last, so the groups list (and its member counts) refetches after the
      // membership changes. Description isn't edited here and is left as is.
      await updateGroup({ groupId: group.id, name: name.trim() }).unwrap();
      toast.success(t("editGroupDialog.success"));
      handleClose();
    } catch (error) {
      setIsSavingMembers(false);
      const message = (error as { data?: { message?: string } })?.data
        ?.message;
      toast.error(message || t("editGroupDialog.error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl p-0 sm:max-w-[500px]">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="text-lg font-semibold">
            {t("editGroupDialog.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("editGroupDialog.nameLabel")} <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder={t("editGroupDialog.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("createGroupDialog.addMembersLabel")}
            </label>
            <Select
              value=""
              onValueChange={addMember}
              disabled={isLoading || allMembersLoading || groupMembersLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    allMembersLoading || groupMembersLoading
                      ? t("createGroupDialog.loading")
                      : t("createGroupDialog.selectMember")
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {allMembers
                  .filter((m) => !selectedMembers.some((s) => s.id === m.id))
                  .map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            {selectedMembers.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-sm"
                  >
                    {member.name}
                    <button
                      type="button"
                      onClick={() => removeMember(member)}
                      aria-label={t("removeGroupMemberDialog.title")}
                      disabled={isLoading}
                      className="cursor-pointer"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex shrink-0 justify-end gap-3 border-t px-6 py-4 pb-8">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
            className="border-primary text-primary hover:text-primary hover:bg-white cursor-pointer"
          >
            {t("editGroupDialog.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || groupMembersLoading}
            loading={isLoading}
            className="bg-primary text-white hover:bg-primary cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("editGroupDialog.save")}
          </Button>
        </DialogFooter>

        {group && (
          <RemoveGroupMemberDialog
            group={group}
            member={memberToRemove}
            onClose={() => setMemberToRemove(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
